import { SelectChangeEvent } from '@mui/material/Select'
import { ReactNode } from 'react'
import {
  Control,
  Controller,
  FieldPath,
  FieldValue,
  FieldValues
} from 'react-hook-form'
import { InputErrorLabel, InputLabelBlock } from 'components/common/TextInput'
import SelectComponent from './SelectComponent'

interface SelectProps<T extends FieldValues> {
  inputLabel?: string
  fieldName: FieldPath<T>
  fullSize?: boolean
  errorMessage?: string
  control?: Control<T>
  defaultValue?: FieldValue<T>
  placeholder: string
  options: {
    display: string | ReactNode
    value: string | number
    key?: string
    disabled?: boolean
    selected?: boolean
  }[]
  multiple?: boolean
  allSelectedMessage?: string
  bold?: boolean
  style?: object
  noMargin?: boolean
  disabled?: boolean
  changeHandler?: (value: SelectChangeEvent<FieldValue<NoInfer<T>>>) => void
  maxHeight?: string
}

export const Select = <T extends FieldValues>({
  fullSize,
  inputLabel,
  errorMessage,
  placeholder,
  control,
  fieldName,
  defaultValue,
  options,
  disabled,
  bold,
  changeHandler,
  multiple,
  style = {},
  noMargin = false,
  maxHeight,
  allSelectedMessage
}: SelectProps<T>) => {
  return (
    <InputLabelBlock fullSize={fullSize} noMargin={noMargin}>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { value, onChange, ref, onBlur } }) => {
          return (
            <SelectComponent
              inputLabel={inputLabel}
              errorMessage={errorMessage}
              placeholder={placeholder}
              options={options}
              disabled={disabled}
              bold={bold}
              style={style}
              noMargin={noMargin}
              multiple={multiple}
              allSelectedMessage={allSelectedMessage}
              onChange={(event) => {
                changeHandler?.(
                  event as SelectChangeEvent<FieldValue<NoInfer<T>>>
                )
                onChange(event)
              }}
              maxHeight={maxHeight}
              value={
                multiple && value === null ? [] : value === null ? '' : value
              }
              inputRef={ref}
              onBlur={onBlur}
            />
          )
        }}
      />
      {errorMessage && <InputErrorLabel>{errorMessage}</InputErrorLabel>}
    </InputLabelBlock>
  )
}
